import { useState, useEffect, useCallback } from 'react';
import {
  DataFeedRowData,
  ProcessedFileResponse,
} from '../../../types/generatedTypes';
import Actions from '../../../store/Actions';
import { processRowWithInstrumentType } from '../utils/danelUtils';
import { ViewMode } from '../../../components/DataFeed/ViewModeToggle/types';
import { ConfigurationTemplate } from '../../../components/DataFeed/Configuration/types';
import { useGridSelectionHandler } from '../../../components/Groups/useGridSelection';
import { RowType } from '../../../types/rowType';

type ProcessedRow = Record<string, any> & { id: string };

interface UseDanelDataProps {
  displayData: DataFeedRowData[];
}

export const useDanelData = ({ displayData }: UseDanelDataProps) => {
  const [rows, setRows] = useState<DataFeedRowData[]>([]);
  const [appliedTemplateId, setAppliedTemplateId] = useState<string | null>(
    null
  );
  const [appliedTemplateConfig, setAppliedTemplateConfig] =
    useState<ConfigurationTemplate | null>(null);
  const [currentFileId, setCurrentFileId] = useState<string | null>(null);
  const [processedFileId, setProcessedFileId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [danelConfig, setDanelConfig] = useState<unknown>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('raw');
  const [processedRows, setProcessedRows] = useState<ProcessedRow[]>([]);
  const [hasProcessedData, setHasProcessedData] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [rowsType, setRowsType] = useState<RowType>(RowType.ROW_TYPE_TRANSACTION);

  // Row selection handler using shared utility
  const handleRowSelectionChange = useGridSelectionHandler(
    selectedRows,
    setSelectedRows,
    displayData,
    {
      getRowId: row => row.id.toString(),
    }
  );

  const loadConfiguration = useCallback(async () => {
    try {
      const config = await Actions.getDanelConfiguration();
      if (config.status === 'success') {
        setDanelConfig(config);
      }
    } catch (error) {
      // Error loading configuration
    }
  }, []);

  const loadProcessedData = useCallback(async () => {
    // Don't fetch processed data if we don't have a current file ID
    if (!currentFileId) {
      setProcessedRows([]);
      setHasProcessedData(false);
      setAppliedTemplateConfig(null);
      setProcessedFileId(null);
      setRowsType(RowType.ROW_TYPE_TRANSACTION);
      return;
    }

    try {
      const data = await Actions.getProcessedFileData(currentFileId);

      // Store the processed_file_id from API response
      setProcessedFileId(data.processed_file_id || null);

      // Track the rows type
      setRowsType(data.rows_type);

      // Check if we have actual processed data (backend now returns has_data field)
      const hasData =
        data.has_data ??
        (data.processed_rows && data.processed_rows.length > 0);

      if (hasData) {
        // Convert processed_rows to grid format with id field and array index
        // Note: Backend's _row_index contains the raw row index for mapping back to DanelFile
        // We preserve it and add _array_index for UI operations
        const rowsWithIds = data.processed_rows.map((row, index) => ({
          ...row,
          id: `processed_${index}`,
          _array_index: index, // Array position for upload operations
          // Preserve backend's _row_index (raw row index) for status updates
        }));
        setProcessedRows(rowsWithIds);
        setHasProcessedData(true);

        // Fetch the applied template configuration
        if (data.template_id) {
          try {
            const templateConfig = await Actions.getConfigurationTemplate(
              data.template_id
            );
            setAppliedTemplateConfig(templateConfig);
          } catch (templateError) {
            console.error(
              'Failed to fetch template configuration:',
              templateError
            );
            setAppliedTemplateConfig(null);
          }
        } else {
          setAppliedTemplateConfig(null);
        }
      } else {
        // No processed data yet - file exists but hasn't been processed
        setProcessedRows([]);
        setHasProcessedData(false);
        setAppliedTemplateConfig(null);
      }
    } catch (error) {
      // Error loading data (e.g., invalid file_id)
      setProcessedRows([]);
      setHasProcessedData(false);
      setAppliedTemplateConfig(null);
      setProcessedFileId(null);
      setRowsType(RowType.ROW_TYPE_TRANSACTION);
    }
  }, [currentFileId]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const fileData = await Actions.getDanelData();
      const rowsWithId = (fileData.rows || []).map(
        (row: DataFeedRowData, index: number) =>
          processRowWithInstrumentType(row, index, danelConfig)
      );
      setRows(rowsWithId);
      setAppliedTemplateId(fileData.applied_template_id || null);
      setCurrentFileId(fileData.file_id || null);
    } catch (error) {
      // Error loading data
    } finally {
      setIsLoading(false);
    }
  }, [danelConfig]);

  const refreshData = useCallback(async () => {
    await loadData();
    await loadProcessedData();
  }, [loadData, loadProcessedData]);

  const updateRow = useCallback(
    async (newRow: DataFeedRowData, oldRow: DataFeedRowData) => {
      // Update UI state immediately for responsiveness
      setRows(prevRows =>
        prevRows.map(row =>
          row.id === newRow.id ? { ...row, ...newRow } : row
        )
      );

      // Find which columns changed and update on server
      const changedColumns = Object.keys(newRow).filter(
        key => newRow[key] !== oldRow[key] && key !== 'id'
      );

      for (const column of changedColumns) {
        await Actions.updateDanelFileCell(
          newRow.id - 1, // Convert to 0-based index
          column,
          newRow[column]
        );
      }

      return newRow;
    },
    []
  );

  const deleteRows = useCallback(
    async (selectedRowIds: string[]) => {
      // Choose correct data source based on view mode
      const sourceRows = viewMode === 'processed' ? processedRows : rows;

      const rowsToDelete = selectedRowIds
        .map(rowIdString => {
          // Find row by string comparison (works for both numeric and string IDs)
          const row = sourceRows.find(r => r.id.toString() === rowIdString);

          // Use _row_index for both views (now consistent naming)
          const originalIndex = row?._row_index as number;

          return {
            rowId: rowIdString,
            originalIndex,
          };
        })
        .filter(item => item.originalIndex !== undefined);

      if (rowsToDelete.length === 0) {
        throw new Error(
          'Could not find original row indices for selected rows'
        );
      }

      // Sort by original index in descending order to prevent index shifting
      const sortedRowsToDelete = rowsToDelete.sort(
        (a, b) => b.originalIndex - a.originalIndex
      );

      // Delete each row using its original index
      for (const { originalIndex } of sortedRowsToDelete) {
        await Actions.deleteDanelRow(originalIndex);
      }

      await refreshData();
    },
    [rows, processedRows, viewMode, refreshData]
  );

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  useEffect(() => {
    if (danelConfig !== null) {
      loadData();
      loadProcessedData();
    }
  }, [danelConfig, loadData, loadProcessedData]);

  return {
    rows,
    appliedTemplateId,
    appliedTemplateConfig,
    currentFileId,
    processedFileId,
    isLoading,
    refreshData,
    updateRow,
    deleteRows,
    danelConfig,
    viewMode,
    setViewMode,
    processedRows,
    hasProcessedData,
    selectedRows,
    setSelectedRows,
    handleRowSelectionChange,
    rowsType,
  };
};
